import { AppConfigType } from "../models/appConfig.model";
import { OrgAppConfig } from "./bootstrap.model";

/** The value of a config: the JSON its column holds, read back; the text itself when it is not JSON; null when it has none. */
export function configValueOf(config: OrgAppConfig | undefined): unknown {
    if (!config?.value) return null;
    try {
        return JSON.parse(config.value);
    } catch {
        return config.value;
    }
}

/** The units a config turns on (`{"kg": true, "lt": true}`), or `defaults` when it is off or turns none on. */
export function enabledUnitsOf(config: OrgAppConfig | undefined, defaults: string[]): string[] {
    const value = config?.enabled ? configValueOf(config) : null;
    if (!value || typeof value !== "object") return defaults;
    const units = Object.entries(value as Record<string, unknown>)
        .filter(([, enabled]) => enabled === true)
        .map(([unit]) => unit);
    return units.length ? units : defaults;
}

/** What the passengers field counts: persons on board, only the passengers, or nothing at all. */
export type Headcount = "pob" | "passengers" | "none";

/**
 * What the organization's configurations (`app_configs`) turn on, read the way the app reads them (`OrgConfigsExtension`):
 * one getter per thing a form shows or hides, so that a form asks `configs.vemdEnabled` and never the type number.
 * A config the organization has no row for is off.
 */
export class OrganizationConfigs {
    constructor(private readonly configs: OrgAppConfig[]) {}

    private of(type: AppConfigType): OrgAppConfig | undefined {
        return this.configs.find((config) => config.type === type);
    }

    private enabled(type: AppConfigType): boolean {
        return this.of(type)?.enabled === true;
    }

    /** A flag of the JSON a config holds (`{"hydraulics": true}`): off when the config is off or has no such key. */
    private flag(type: AppConfigType, key: string): boolean {
        const config = this.of(type);
        const value = config?.enabled ? configValueOf(config) : null;
        return !!value && typeof value === "object" && (value as Record<string, unknown>)[key] === true;
    }

    /** The parts of the passengers config's value, `1-100-km`: paying passengers on or off, the taxable distance and its unit. */
    private passengerParts(): string[] {
        const config = this.of(AppConfigType.passengers);
        return config?.enabled && typeof config.value === "string" ? config.value.trim().split("-") : [];
    }

    get enginePowerCheckEnabled(): boolean {
        return this.enabled(AppConfigType.enginePowerCheck);
    }

    get penaltyFactorEnabled(): boolean {
        return this.enabled(AppConfigType.penaltyFactor);
    }

    get crewEnabled(): boolean {
        return this.enabled(AppConfigType.crew);
    }

    get specificationsEnabled(): boolean {
        return this.enabled(AppConfigType.flightSpecifications);
    }

    get typeNotesEnabled(): boolean {
        return this.enabled(AppConfigType.flightTypeNotes);
    }

    get operationsEnabled(): boolean {
        return this.enabled(AppConfigType.flightOperations);
    }

    get multiStopFlightEnabled(): boolean {
        return this.enabled(AppConfigType.hotRefuelFlight);
    }

    get positioningFlightEnabled(): boolean {
        return this.enabled(AppConfigType.positioningFlight);
    }

    get vemdEnabled(): boolean {
        return this.enabled(AppConfigType.vemd);
    }

    /** HYD oil. */
    get hydraulicsEnabled(): boolean {
        return this.flag(AppConfigType.enginesForm, "hydraulics");
    }

    /** MGB and TGB oil. */
    get drivetrainEnabled(): boolean {
        return this.flag(AppConfigType.enginesForm, "drivetrain");
    }

    get passengerEnabled(): boolean {
        return this.enabled(AppConfigType.passengers);
    }

    /** Persons on board replace the passengers, and only where the passengers are on. */
    get pobEnabled(): boolean {
        return this.passengerEnabled && this.enabled(AppConfigType.pob);
    }

    get payingPassengersEnabled(): boolean {
        return this.passengerParts()[0] === "1";
    }

    /** The tax needs paying passengers and a taxable distance: `1-100-km`. */
    get taxEnabled(): boolean {
        const parts = this.passengerParts();
        return this.payingPassengersEnabled && parts.length === 3 && !!parts[1] && parts[1] !== "0";
    }

    get headcount(): Headcount {
        if (!this.passengerEnabled) return "none";
        return this.pobEnabled ? "pob" : "passengers";
    }
}
